import React, {
  useEffect,
} from 'react';
import clsx from 'clsx';
import {
  RoomSettingDTO,
} from 'dtos';
import {
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import AddIcon from '@mui/icons-material/Add';
import {
  useGetAppointmentStatuses,
  useGetRoomSettings,
  useUpsertRoom,
  useDeleteRoom,
} from 'views/EMR/Settings/hooks';
import Divider from '@mui/material/Divider';
import VirtualList from 'core/components/VirtualList';
import Room from 'views/EMR/Settings/Appointments/Room';
import Button from 'pages/Dashboard/components/Button';
import MediaListSkeleton
  from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/MediaListSkeleton';
import ToolTip from 'core/components/Tooltip';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useAppointmentStatusColors,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  lightenHexColor,
} from 'utils/colors';

type RoomsForm = {
  rooms: RoomSettingDTO[];
};

export default function AppointmentStatusSettings() {
  const { data: rooms, refetch, isLoading: loadingRooms } = useGetRoomSettings();
  const { data: statuses, isLoading: loadingStatuses } = useGetAppointmentStatuses();
  const colors = useAppointmentStatusColors();
  const { mutateAsync: upsertRoom, isPending: upserting } = useUpsertRoom();
  const { mutateAsync: deleteRoom, isPending: deleting } = useDeleteRoom();
  const form = useForm<RoomsForm>({ mode: 'all' });
  const { reset, control, formState: { isValid, isDirty } } = form;

  useEffect(() => {
    reset({ rooms }, { keepDirty: false, keepTouched: false });
  }, [rooms]);

  const { fields: roomList, append, remove } = useFieldArray({ name: 'rooms', control });
  const onUpdate = async (index: number, value: RoomSettingDTO) => {
    if (isValid) {
      await upsertRoom(value);
      settingsEventChannel.emit('onRoomSettingsChange');
      await refetch();
    }
  };

  const onRemove = async (index: number, value: RoomSettingDTO) => {
    remove(index);
    if (!isNil(value.roomSettingId)) {
      await deleteRoom(value);
      settingsEventChannel.emit('onRoomSettingsChange');
      await refetch();
    }
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="w-80 h-full overflow-hidden flex flex-col gap-2">
        <h3 className="text-sm font-semibold px-4">Built-in statuses</h3>
        {loadingStatuses && <MediaListSkeleton />}
        {!loadingStatuses && (
          <VirtualList<string>
            data={statuses ?? []}
            itemSize={32}
            className="font-semibold text-xs pb-4 pl-2"
            rowClassName="border-b border-b-gray-200 last:border-0 p-2 hover:bg-blue-50 cursor-pointer"
            renderItemComponent={(status) => (
              <div className="w-full flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: lightenHexColor(colors[status] ?? '', 50) }} />
                <span className="text-xs">{status}</span>
              </div>
            )}
          />
        )}
      </div>
      <Divider flexItem orientation="vertical" className="!p-0" />
      <div className={clsx('w-80 h-full overflow-hidden flex flex-col gap-2 relative', {
        'justify-between': !loadingRooms && roomList.length > 0,
      })}
      >
        <h3 className="text-sm font-semibold px-4">Rooms</h3>
        {loadingRooms && <MediaListSkeleton />}
        {!loadingRooms && (
          <FormProvider {...form}>
            <VirtualList<RoomSettingDTO>
              renderItemComponent={(room, index) => (
                <Room
                  key={room.roomSettingId}
                  value={room}
                  index={index}
                  update={onUpdate}
                  remove={onRemove}
                  color={colors[room?.name ?? ''] ?? ''}
                />
              )}
              rowClassName="border-b border-b-gray-200 last:border-0"
              data={roomList ?? []}
              itemSize={32}
            />
            <div className="m-4">
              <ToolTip
                title="Room name is missing"
                hide={isValid}
              >
                <div className={clsx({ 'cursor-not-allowed': !isValid })}>
                  <Button
                    variant="outlined"
                    className={clsx('!border-dashed !block !w-full !max-w-[unset]', {
                      '!border-blue-500': !upserting && !deleting,
                      '!border-gray-300': !isValid || isDirty,
                    })}
                    color="primary"
                    disabled={!isValid || upserting || deleting || isDirty}
                    onClick={() => { append({ name: '' }); }}
                  >
                    <div className="flex gap-2 justify-center w-full items-center">
                      {!upserting && !deleting
                        ? (
                          <>
                            <AddIcon className="!w-4 !h-4 !text-inherit" />
                            Add Room
                          </>
                        )
                        : (
                          <>
                            <Spinner />
                            In progress...
                          </>
                        )}
                    </div>
                  </Button>
                </div>
              </ToolTip>
            </div>
          </FormProvider>
        )}
      </div>
    </div>
  );
}
